<template><slot></slot></template>
<script lang="ts" setup>
import { deleteEmptyKey } from "@p-v/utils"
import { createIcon, createLabel, createSize, createSymbol } from "@p-v/utils/factory"
import { inject, onUnmounted, ref, watch } from "vue"
import type { Point, Size, Icon, Label, SymbolIcon } from "@p-v/packages/Map/map"

interface ComponentProps {
  position: Point //	标注的位置
  offset?: Size //	标注的偏移值
  icon?: Icon | SymbolIcon //	标注的图标
  massClear?: boolean //	是否在调用map.clearOverlays清除此覆盖物，默认为true
  dragging?: boolean //	是否可拖拽，默认为false
  clicking?: boolean //	是否可点击，默认为true
  raiseOnDrag?: boolean //	拖拽标注时，标注是否开启离开地图表面效果，默认为true
  draggingCursor?: string //	拖拽标注时的鼠标指针样式。此属性值需遵循CSS的cursor属性规范
  rotation?: number //	标注的旋转角度
  shadow?: Icon //	阴影图标
  title?: string //	鼠标移到marker上的显示内容
  label?: Label //	为标注添加文本标注
  animation?: string //	标注的动画效果
  top?: boolean //	将标注置于其他标注之上。默认情况下，纬度较低的标注会覆盖在纬度较高的标注之上，从而形成一种立体效果。通过此方法可使某个标注覆盖在其他所有标注之上。注意：如果在多个标注对象上调用此方法，则这些标注依旧按照纬度产生默认的覆盖效果。
  zIndex?: number //	标注的z-index值
}

const props = withDefaults(defineProps<ComponentProps>(), {
  position: () => ({ lng: 116.404, lat: 39.915 }),
  offset: () => ({ width: 0, height: 0 }),
  icon: undefined,
  massClear: () => true,
  dragging: () => false,
  clicking: () => true,
  raiseOnDrag: () => true,
  draggingCursor: undefined,
  rotation: () => 0,
  shadow: undefined,
  title: () => "",
  label: undefined,
  animation: () => "",
  top: () => false,
  zIndex: () => 0,
})

const bmap = inject("bmap") as any

const overlay = ref<any>(undefined)

watch(
  () => bmap.value,
  value => {
    if (value) {
      if (overlay.value) {
        bmap.value?.removeOverlay(overlay.value)
      }
      let icon = undefined
      if (props.icon) {
        // 判断是否是SymbolIcon
        if ((props.icon as SymbolIcon).path) {
          icon = createSymbol(window.BMapGL, props.icon as SymbolIcon)
        } else {
          icon = createIcon(window.BMapGL, props.icon as Icon)
        }
      }
      let makerOption = {
        offset: props.offset && createSize(window.BMapGL, props.offset),
        icon,
        enableMassClear: props.massClear,
        enableDragging: props.dragging,
        enableClicking: props.clicking,
        raiseOnDrag: props.raiseOnDrag,
        draggingCursor: props.draggingCursor,
        rotation: props.rotation,
        shadow: props.shadow,
        title: props.title,
      }
      deleteEmptyKey(makerOption)
      overlay.value = new window.BMapGL.Marker(new window.BMapGL.Point(props.position.lng, props.position.lat), makerOption)

      props.label && overlay.value && overlay.value.setLabel(createLabel(window.BMapGL, props.label))

      overlay.value.setTop(props.top)
      overlay.value.setZIndex(props.zIndex)
      overlay.value.setAnimation(window[props.animation])
      bmap.value?.addOverlay(overlay.value)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  bmap.value?.removeOverlay(overlay.value)
})
</script>
